import { Form, Formik } from "formik";
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  createUserRequest,
  saveWhitelistingRequest,
  updateUserRequest,
  updateWhitelistingRequest,
} from "../../api/users.api";
import {
  MESSAGE_TYPE,
  whitelistingStatusColor,
} from "../../common/constants/common.constants";
import {
  IUpdateUserAccountDto,
  IUserAccount,
} from "../../common/interfaces/user.interface";
import { UserContext } from "../../contexts/userContext";
import {
  mapSocialsToWizardFields,
  mapUserSettingsFormValuesToUpdateUserAccountDto,
  mapUserSettingsFormValuesToUserAccountDto,
  mapUserSettingsFromToWhitelistingDto,
  signTransactionMessage,
} from "../../utilities/helpers";
import { createNotification } from "../../utilities/notification";

import BasicInfo from "./BasicInfo/BasicInfo";
import SocialNetworks from "./SocialNetworks/SocialNetworks";
import formModel from "./formConfig";
import "./UserSettingsForm.scss";
import { validateUserSettingsForm } from "./validators";

import { useAnchorWallet, useWallet } from "@solana/wallet-adapter-react";
import { encode } from "@project-serum/anchor/dist/cjs/utils/bytes/bs58";
import SubmitButton from "../SubmitButton/SubmitButton";
import ErrorNotification from "../ErrorNotification/ErrorNotification";
import WhitelistingRequest from "./WhitelistingRequest/WhitelistingRequest";
import {
  MonkeAdminStatus,
  TermsConditionsType,
  WhitelistingStatus,
} from "../../common/enums/whitelisting.enums";
import RequiredCheckbox from "../RequiredCheckbox/RequiredCheckbox";
import { termsAndConditionsConfig } from "../../common/constants/whitelisting.constants";

const initialValues = {};
Object.keys(formModel.formFields).map(
  (key) => (initialValues[key] = formModel.formFields[key].initialValue)
);

interface UserSettingsFormProps {
  closeModal?: () => void;
}

const UserSettingsForm: FC<UserSettingsFormProps> = ({ closeModal }) => {
  const { updateUser, user, isWhitelisted, adminData } =
    useContext(UserContext);
  const wallet = useAnchorWallet();
  const payerWallet = useWallet();
  const { formId, formFields } = formModel;
  const [termsAndConditions, setTermsAndConditions] = useState(
    termsAndConditionsConfig
  );

  const socialNetworks = mapSocialsToWizardFields(formFields);

  const createNewUser = async (values) => {
    const userData: IUserAccount = mapUserSettingsFormValuesToUserAccountDto(
      values,
      wallet!
    );
    try {
      const newUser: IUserAccount | undefined = await createUserRequest(
        userData
      );
      createNotification(MESSAGE_TYPE.SUCCESS, "Changes saved");
      if (newUser) {
        updateUser(newUser);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateUserAccount = async (values) => {
    let signature: string;
    try {
      signature = await signTransactionMessage(
        "Update user account data",
        payerWallet,
        values.isLedger
      );
    } catch (error: any) {
      createNotification(
        "error",
        error.message || error.error || "Signing failed!"
      );
      return null;
    }
    const updateUserAccountData: IUpdateUserAccountDto =
      mapUserSettingsFormValuesToUpdateUserAccountDto(
        values,
        wallet!,
        signature
      );
    try {
      const updatedUser: IUserAccount | undefined = await updateUserRequest(
        updateUserAccountData,
        wallet!.publicKey.toString()
      );
      if (updatedUser) {
        updateUser({ ...updatedUser, whitelisting: user?.whitelisting });
        createNotification(MESSAGE_TYPE.SUCCESS, "Changes saved");
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Failed to save changes");
      console.log(error);
    }
  };

  const createNewUserWithWhitelisting = useCallback(
    async (values) => {
      try {
        const signature = await signTransactionMessage(
          "Create whitelisting request",
          payerWallet,
          values.isLedger
        );
        const saveWhitelistingDto = mapUserSettingsFromToWhitelistingDto(
          values,
          wallet!,
          signature
        );
        if (user) saveWhitelistingDto.user = undefined;
        const savedData = await saveWhitelistingRequest(saveWhitelistingDto);
        updateUser({ ...savedData.user, whitelisting: savedData });

        createNotification(
          MESSAGE_TYPE.SUCCESS,
          "Successfully saved whitelisting request"
        );
      } catch (error: any) {
        console.log(error);

        createNotification(
          MESSAGE_TYPE.ERROR,
          "Failed to create new whitelisting request"
        );
      }
    },
    [user]
  );

  const updateWhitelisting = useCallback(
    async (values) => {
      try {
        const signature = await signTransactionMessage(
          "Update whitelisting request",
          payerWallet,
          values.isLedger
        );
        const saveWhitelistingDto = mapUserSettingsFromToWhitelistingDto(
          values,
          wallet!,
          signature,
          user?.whitelisting?.id
        );

        const updatedUser = await updateWhitelistingRequest(
          saveWhitelistingDto
        );

        if (updatedUser) {
          updateUser(updatedUser);
          createNotification(
            MESSAGE_TYPE.SUCCESS,
            "Successfully updated whitelisting request"
          );
        }
      } catch (error: any) {
        console.log(error);
        createNotification(MESSAGE_TYPE.ERROR, "Failed to update notification");
      }
    },
    [wallet, user]
  );

  const submitForm = useCallback(
    async (values) => {
      if (
        (!user?.whitelisting ||
          user?.whitelisting.status === WhitelistingStatus.Denied) &&
        termsAndConditions.find((item) => !item.isChecked) &&
        !adminData
      ) {
        setTermsAndConditions(
          termsAndConditions.map((item) => {
            return {
              ...item,
              touched: true,
            };
          })
        );
        return;
      }
      if (closeModal) {
        await createNewUser(values);
        closeModal!();
      } else {
        if (
          (!user && !isWhitelisted && !adminData) ||
          (user && !user.whitelisting && !isWhitelisted && !adminData)
        )
          await createNewUserWithWhitelisting(values);
        else if (
          user &&
          user.whitelisting &&
          user.whitelisting.status === WhitelistingStatus.Denied
        ) {
          await updateWhitelisting(values);
        } else if (
          user &&
          (isWhitelisted ||
            (user.whitelisting &&
              user.whitelisting.status === WhitelistingStatus.Pending) ||
            (adminData && adminData.status === MonkeAdminStatus.Accepted))
        ) {
          await updateUserAccount(values);
        } else {
          await createNewUser(values);
        }
      }
    },
    [user, isWhitelisted, termsAndConditions]
  );

  const agreeToTermAndConditions = useCallback(
    (type: TermsConditionsType, currentState: boolean) => {
      const addedTermsAndConditions = [...termsAndConditions];
      const itemIndex = addedTermsAndConditions.findIndex(
        (item) => item.type === type
      );
      addedTermsAndConditions[itemIndex] = {
        ...termsAndConditions[itemIndex],
        isChecked: !currentState,
        touched: true,
      };
      setTermsAndConditions(addedTermsAndConditions);
    },
    [termsAndConditions]
  );

  const renderTermsAndConditions = useMemo(() => {
    return termsAndConditions.map((config) => {
      return (
        <RequiredCheckbox
          handleClick={() =>
            agreeToTermAndConditions(config.type, config.isChecked)
          }
          isChecked={config.isChecked}
          isInErrorState={!config.isChecked && config.touched}
          message={config.message}
        />
      );
    });
  }, [termsAndConditions]);

  const skipForNow = async () => {
    await createNewUser(initialValues);
    closeModal!();
  };

  const whitelistingData = useMemo(() => {
    if (user?.whitelisting) {
      return whitelistingStatusColor.get(user.whitelisting.status);
    }
  }, [user, whitelistingStatusColor]);

  return (
    <div className="user-settings">
      <Formik
        validate={(values) => validateUserSettingsForm(values, !!adminData)}
        initialValues={initialValues}
        onSubmit={submitForm}
      >
        {({ isSubmitting, touched, errors, values }) => (
          <Form id={formId}>
            {whitelistingData &&
              (!adminData ||
                (adminData &&
                  adminData.status === MonkeAdminStatus.Rejected)) && (
                <div
                  className="whitelisting-request__status"
                  style={{
                    backgroundColor: whitelistingData[0],
                    width: "100%",
                    borderRadius: "8px",
                    padding: "1em",
                  }}
                >
                  <p
                    style={{
                      color: whitelistingData[1],
                      fontWeight: "600",
                    }}
                  >
                    {whitelistingData[2]}
                  </p>
                </div>
              )}

            {user?.whitelisting?.status !== WhitelistingStatus.Pending && (
              <>
                <BasicInfo
                  emailField={formFields.email}
                  usernameField={formFields.username}
                  userImageField={formFields.userImage}
                  country={formFields.country}
                  isModal={!!closeModal}
                />

                <SocialNetworks socialNetworks={socialNetworks} />
              </>
            )}

            {!isWhitelisted &&
              !adminData &&
              user?.whitelisting?.status !== WhitelistingStatus.Pending && (
                <WhitelistingRequest
                  monkeInterests={formFields.monkeInterests}
                  personalBackground={formFields.personalBackground}
                  areasOfInterest={formFields.areasOfInterest}
                  domainOfExpertise={formFields.domainOfExpertise}
                  disabled={!!user && !!user.whitelisting}
                />
              )}

            {(!user?.whitelisting ||
              user.whitelisting.status === WhitelistingStatus.Denied) &&
              !adminData && (
                <div className="user-settings__terms-conditions">
                  {renderTermsAndConditions}
                </div>
              )}

            {user?.whitelisting?.status !== WhitelistingStatus.Pending && (
              <div className="user-settings__actions">
                {closeModal && (
                  <button
                    onClick={skipForNow}
                    className="user-settings__actions--skip"
                    type="button"
                  >
                    Skip for now
                  </button>
                )}
                <SubmitButton
                  disabled={isSubmitting}
                  type="submit"
                  className="user-settings__save-changes"
                  submitting={isSubmitting}
                  onClick={() => {}}
                >
                  Save changes
                </SubmitButton>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserSettingsForm;
